(() => {
  'use strict';

  function install() {
  const CPO_PATTERN = /Certified\s+Pre[-\s]?Owned/i;
  const DONOR_PATTERN = /#\d+/;
  const provider = document.querySelector('product-variant-provider[product-page]');
  const handle = window.location.pathname.match(/\/products\/([^/?#]+)/i)?.[1];
  if (!provider || !handle || provider.dataset.xpartBuyingOptionsInstalled) return;
  let config;
  try { config = JSON.parse(provider.querySelector('[data-xpart-buying-options-config]')?.textContent || 'null'); } catch { return; }
  if (!config?.strings) return;
  provider.dataset.xpartBuyingOptionsInstalled = 'true';
  const controller = new AbortController();
  const t = (key, values = {}) => Object.entries(values).reduce((text, [name, value]) => text.split(`__${name}__`).join(String(value)), config.strings[key] || '');
  const buyingError = key => Object.assign(new Error(t(key)), { xpartMessage: true });

  let productData = null;
  let aggregateCpo = false;
  let selecting = false;
  let submitting = false;
  let internalSelection = false;
  let repairQueued = false;
  const replayForms = new WeakSet();

  const cleanText = value => String(value || '').replace(/\s+/g, ' ').trim();
  const shopRoot = () => window.Shopify?.routes?.root || '/';
  const variantTitle = variant => cleanText(variant?.public_title || variant?.title);
  const isCpo = variant => CPO_PATTERN.test(variantTitle(variant));
  const variants = () => Array.isArray(productData?.variants) ? productData.variants : [];
  const findVariant = id => variants().find(variant => String(variant.id) === String(id));
  const mainForm = () => provider.querySelector('product-form [data-product-form-targeter]')?.form || provider.querySelector('form[action*="/cart/add"]');
  const nativeButton = form => form?.querySelector('product-to-cart[form-child] [data-to-cart]');
  const formVariantId = form => form ? String(new FormData(form).get('id') || '') : '';
  const setText = (element, value) => {
    if (element && element.textContent !== value) element.textContent = value;
  };

  function getLabel(input) {
    if (!input) return null;
    return input.closest('label') || (input.id ? provider.querySelector(`label[for="${CSS.escape(input.id)}"]`) : null);
  }

  function optionInputs() {
    return Array.from(provider.querySelectorAll('product-variant-selector input[type="radio"][data-variant-id]'));
  }

  function selectedVariantId() {
    if (productData?.options?.length === 1) {
      const selected = optionInputs().find(input => input.checked);
      if (selected?.dataset.variantId) return String(selected.dataset.variantId);
    }
    return formVariantId(mainForm());
  }

  function getAvailableCpoVariants() {
    const available = variants().filter(variant => isCpo(variant) && variant.available === true);
    const donors = available.filter(variant => DONOR_PATTERN.test(variantTitle(variant)));
    return donors.length ? donors : available;
  }

  async function loadProductData() {
    const response = await fetch(`${shopRoot()}products/${encodeURIComponent(decodeURIComponent(handle))}.js?xpart=${Date.now()}`, {
      method: 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw buyingError('check_failed');
    const data = await response.json();
    if (!Array.isArray(data?.variants)) throw buyingError('refresh');
    productData = data;
    return data;
  }

  function money(cents) {
    const currency = window.Shopify?.currency?.active || config.currency;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || config.locale, { style: 'currency', currency }).format(Number(cents) / 100);
    } catch {
      return `$${(Number(cents) / 100).toFixed(2)}`;
    }
  }

  function getDisplayPrice(available) {
    if (!available.length) return '';
    const prices = available.map(variant => Number(variant.price)).filter(Number.isFinite);
    if (!prices.length) return '';
    const lowest = Math.min(...prices);
    return new Set(prices).size === 1 ? money(lowest) : t('from_price', { price: money(lowest) });
  }

  function showError(message) {
    let error = provider.querySelector('[data-xpart-buying-error]');
    if (!error) {
      error = document.createElement('p');
      error.dataset.xpartBuyingError = '';
      error.setAttribute('role', 'alert');
      const form = mainForm();
      if (form) form.after(error);
      else provider.appendChild(error);
    }
    setText(error, message);
    error.hidden = !message;
  }

  function clearError() {
    const error = provider.querySelector('[data-xpart-buying-error]');
    if (error) {
      error.hidden = true;
      setText(error, '');
    }
  }

  function waitForVariant(id, timeout = 6000) {
    return new Promise((resolve, reject) => {
      const matches = () => formVariantId(mainForm()) === String(id) && String(provider.getAttribute('initial-variant-id')) === String(id);
      if (matches()) return resolve();
      let timer;
      const observer = new MutationObserver(() => {
        if (matches()) {
          observer.disconnect();
          clearTimeout(timer);
          resolve();
        }
      });
      observer.observe(provider, { childList: true, subtree: true, attributes: true });
      timer = setTimeout(() => {
        observer.disconnect();
        reject(buyingError('updating'));
      }, timeout);
    });
  }

  async function selectNativeVariant(id) {
    const input = optionInputs().find(option => String(option.dataset.variantId) === String(id));
    if (!input || input.disabled) throw buyingError('refresh_option');
    internalSelection = true;
    try {
      if (!input.checked) input.click();
    } finally {
      internalSelection = false;
    }
    await waitForVariant(id);
  }

  function findSafeContainer() {
    if (productData?.options?.length !== 1) return null;
    const input = optionInputs().find(option => isCpo(findVariant(option.dataset.variantId)));
    return input?.closest('product-variant-selector')?.querySelector('[class~="#product-variant-selector-item-body"]') || getLabel(input)?.parentElement || null;
  }

  function hideOriginalCpoChoices() {
    optionInputs().forEach(input => {
      if (!isCpo(findVariant(input.dataset.variantId))) return;
      const label = getLabel(input);
      if (!label) return;
      label.hidden = true;
      label.style.display = 'none';
      input.tabIndex = -1;
    });
  }

  function updateCpoPicker() {
    const container = findSafeContainer();
    if (!container) return;
    const available = getAvailableCpoVariants();
    let picker = provider.querySelector('.xpart-cpo-picker');
    if (!available.length) {
      picker?.remove();
      hideOriginalCpoChoices();
      return;
    }
    if (!picker) {
      picker = document.createElement('div');
      picker.className = 'xpart-cpo-picker';
      picker.dataset.xpartCpoCustom = 'true';
      const heading = document.createElement('div');
      heading.className = 'xpart-cpo-picker__heading';
      const label = document.createElement('span');
      label.className = 'xpart-cpo-picker__heading-label';
      label.textContent = t('cpo_heading') + ' ';
      const count = document.createElement('strong');
      count.className = 'xpart-cpo-picker__heading-value';
      heading.append(label, count);
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'xpart-cpo-trigger';
      const content = document.createElement('span');
      content.className = 'xpart-cpo-trigger__content';
      const text = document.createElement('span');
      text.className = 'xpart-cpo-trigger__text';
      const price = document.createElement('span');
      price.className = 'xpart-cpo-trigger__price';
      content.append(text, price);
      button.appendChild(content);
      button.addEventListener('click', selectCpo);
      picker.append(heading, button);
      container.appendChild(picker);
    }
    hideOriginalCpoChoices();
    const selected = findVariant(selectedVariantId());
    const pressed = isCpo(selected);
    const button = picker.querySelector('button');
    button.setAttribute('aria-pressed', String(pressed));
    button.disabled = selecting || submitting;
    setText(picker.querySelector('.xpart-cpo-picker__heading-value'), t('available_count', { count: available.length }));
    setText(picker.querySelector('.xpart-cpo-trigger__text'), selecting ? t('selecting_cpo') : pressed ? t('cpo_selected') : t('select_cpo'));
    setText(picker.querySelector('.xpart-cpo-trigger__price'), pressed ? money(selected.price) : getDisplayPrice(available));
  }

  async function selectCpo(event) {
    event.preventDefault();
    event.stopPropagation();
    if (selecting || submitting) return;
    selecting = true;
    clearError();
    updateCpoPicker();
    try {
      await loadProductData();
      const available = getAvailableCpoVariants();
      if (!available.length) throw buyingError('no_cpo');
      const selected = available.find(variant => String(variant.id) === selectedVariantId()) || available[0];
      aggregateCpo = true;
      await selectNativeVariant(selected.id);
    } catch (error) {
      showError(error?.xpartMessage ? error.message : t('select_failed'));
    } finally {
      selecting = false;
      updateCpoPicker();
    }
  }

  function resetNativeLoading(form) {
    const control = form?.querySelector('product-to-cart[form-child]');
    if (control && 'loading' in control) control.loading = false;
  }

  function lockChoices() {
    const controls = Array.from(provider.querySelectorAll('product-variant-selector input, product-variant-selector select'));
    const states = controls.map(control => [control, control.disabled]);
    controls.forEach(control => { control.disabled = true; });
    return () => states.forEach(([control, disabled]) => { control.disabled = disabled; });
  }

  function watchNativeResult(button, originalDisabled, releaseChoices) {
    const observer = new MutationObserver(() => {
      if (!button.classList.contains('!loading')) {
        observer.disconnect();
        button.disabled = originalDisabled;
        releaseChoices();
        submitting = false;
        updateCpoPicker();
      }
    });
    observer.observe(button, { attributes: true, attributeFilter: ['class'] });
    return observer;
  }

  async function handleSubmit(event) {
    const form = event.target;
    if (replayForms.has(form)) return;
    if (!provider.contains(form) || !form.querySelector('product-form') || event.defaultPrevented) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (submitting || selecting) return;
    submitting = true;
    clearError();
    updateCpoPicker();
    let delegated = false;
    let releaseChoices = () => {};
    try {
      let id = selectedVariantId();
      if (!id) throw buyingError('choose_option');
      await waitForVariant(id);
      await loadProductData();
      if (selectedVariantId() !== id) throw buyingError('selection_changed');
      let variant = findVariant(id);
      if (variant?.available !== true && isCpo(variant) && aggregateCpo) {
        variant = getAvailableCpoVariants()[0];
        if (variant) {
          id = String(variant.id);
          await selectNativeVariant(id);
        }
      }
      if (variant?.available !== true) throw buyingError('unavailable');
      const currentForm = mainForm();
      if (formVariantId(currentForm) !== id) throw buyingError('wait_selected');
      const quantity = Number(new FormData(currentForm).get('quantity') || 1);
      if (!Number.isSafeInteger(quantity) || quantity < 1) throw buyingError('invalid_quantity');
      const component = currentForm.querySelector('product-form');
      const control = currentForm.querySelector('product-to-cart[form-child]');
      const button = nativeButton(currentForm);
      if (!button || !control || typeof component?._submitHandler !== 'function' || typeof control.addToCart !== 'function') throw buyingError('cart_loading');
      const originalDisabled = button.disabled;
      control.loading = true;
      releaseChoices = lockChoices();
      const observer = watchNativeResult(button, originalDisabled, releaseChoices);
      const addToCart = control.addToCart;
      let nativeSubmitted = false;
      control.addToCart = function (...args) {
        nativeSubmitted = true;
        return addToCart.apply(this, args);
      };
      replayForms.add(currentForm);
      try {
        currentForm.requestSubmit(button);
        delegated = nativeSubmitted;
        if (delegated) button.disabled = true;
        else observer.disconnect();
      } catch (error) {
        observer.disconnect();
        throw error;
      } finally {
        control.addToCart = addToCart;
        replayForms.delete(currentForm);
      }
    } catch (error) {
      showError(error?.xpartMessage ? error.message : t('buying_failed'));
    } finally {
      if (!delegated) {
        releaseChoices();
        submitting = false;
        resetNativeLoading(mainForm());
        updateCpoPicker();
      }
    }
  }

  function scheduleRepair() {
    if (repairQueued) return;
    repairQueued = true;
    requestAnimationFrame(() => {
      repairQueued = false;
      updateCpoPicker();
    });
  }

  provider.addEventListener('change', event => {
    if (!event.target.matches('product-variant-selector input[type="radio"], product-variant-selector select')) return;
    if (!internalSelection) aggregateCpo = false;
    clearError();
    scheduleRepair();
  }, { signal: controller.signal });
  document.addEventListener('submit', handleSubmit, { capture: true, signal: controller.signal });
  const repairObserver = new MutationObserver(scheduleRepair);
  repairObserver.observe(provider, { childList: true, subtree: true, attributes: true, attributeFilter: ['initial-variant-id'] });
  document.addEventListener('shopify:section:unload', event => {
    if (event.target.contains(provider)) {
      controller.abort();
      repairObserver.disconnect();
    }
  }, { signal: controller.signal });
  loadProductData().then(updateCpoPicker).catch(() => {});
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', install, { once: true });
  else install();
  document.addEventListener('shopify:section:load', install);
})();
